import CreatorProfileInstagram, { CreatorProfileInput } from '@/interfaces/creator-profile.interface'
import { SocialNetworkProfile } from '@/interfaces/social-network-profiles.interface'
import { FacebookDiscoveryUser } from '@/interfaces/user.interface'
import DataLayerHelper from '@helpers/data-layer.helper'
import Environment from '@interfaces/environment.interface'
import Notification from '@interfaces/notification.interface'
import onEnvironmentChange from '@observables/environment.observable'
import LogService from '@services/log/log.service'
import MainService from '@services/main/main.service'
import { AxiosError, AxiosResponse } from 'axios'
import { take } from 'rxjs/operators'
export default class HubCoreService extends MainService {
  url: string
  logService = new LogService()
  dataLayerHelper = new DataLayerHelper()

  constructor(hubCoreUrl?: string) {
    super()
    if (!hubCoreUrl) {
      onEnvironmentChange.pipe(take(1)).subscribe((environment: Environment) => {
        this.url = environment?.squidApis?.hubCore
      })
    } else {
      this.url = hubCoreUrl
    }
    this.logService.init('Hub Core Service')
  }

  async bindSocialNetwork(code: string, socialNetwork: string, id = null): Promise<AxiosResponse> {
    let headers
    if (socialNetwork === 'auth0') {
      headers = await this.getHeaders(false, '', false)
    } else {
      headers = await this.getHeaders()
    }

    if (!code) throw new Error('code is required')
    if (!socialNetwork) throw new Error('socialNetwork is required')

    const URL = `${this.url}/bind/${socialNetwork}`

    try {
      const response = await this.http.post(URL, { code, idInstagram: id }, { headers })
      await this.dataLayerHelper.sendSuccessToDataLayer({ socialNetwork })
      return response
    } catch (error) {
      this.logService.error({ message: 'Error to binding social network', error })
      await this.dataLayerHelper.sendErrorToDataLayer({ errorCode: error?.response?.data, socialNetwork })
      throw error?.response?.data || error
    }
  }

  async getInstagramList(code: string) {
    const headers = await this.getHeaders()
    return this.http
      .get(`${this.url}/bind/facebook/pages?code=${code}`, { headers })
      .then((r: AxiosResponse) => r)
      .catch((error: any) => {
        this.logService.error({ message: 'Error to get instagram list', error })
        throw error
      })
  }

  public async getNotifications(page = 1, limit = 100): Promise<Array<Notification>> {
    const headers = await this.getHeaders()
    return this.http
      .get(`${this.url}/notifications/hub?limit=${limit}&page=${page}`, { headers })
      .then((r: AxiosResponse) => r.data)
      .catch((error: any) => {
        this.logService.error({ message: 'Error on get notifications', error })
        throw error.response
      })
  }

  public async getCreatorProfileBySquidId(
    squidId: string,
    onlyActive = false,
    page = 1,
    pageSize = 5
  ): Promise<Array<CreatorProfileInstagram>> {
    const headers = await this.getHeaders(false)
    return this.http
      .get(`${this.url}/creators-insights/${squidId}/profiles?onlyActive=${onlyActive}&page=${page}&pageSize=${pageSize}`, { headers })
      .then((r: AxiosResponse) => r.data)
      .catch((error: AxiosError) => {
        this.logService.error({
          message: 'Error on get creator profile by squidId',
          error,
        })
        throw error.response
      })
  }

  public async activateCreatorProfile(profileId: string, activateCreatorProfileDto: CreatorProfileInput): Promise<void> {
    const headers = await this.getHeaders()
    return this.http
      .patch(`${this.url}/creators-insights/${profileId}/activation`, activateCreatorProfileDto, { headers })
      .then((r: AxiosResponse) => r.data)
      .catch((error: AxiosError) => {
        this.logService.error({
          message: 'Error on activate creator profile',
          error,
        })
        throw error.response
      })
  }

  public async generateToken(tokenPayload: string): Promise<{ link: string }> {
    const headers = await this.getHeaders()

    return this.http
      .post(`${this.url}/creators-insights/token`, { tokenPayload }, { headers })
      .then((r: AxiosResponse) => r.data || {})
      .catch((error: AxiosError) => {
        this.logService.error({
          message: 'Error on generate auth token',
          error,
        })
        throw error.response
      })
  }

  public async renewToken(code: string, socialNetwork: string, idInstagram: string = null) {
    if (!code) {
      throw new Error('code is required')
    }
    if (!socialNetwork) {
      throw new Error('socialNetwork is required')
    }
    const headers = await this.getHeaders()
    const payload = {
      code,
    }
    if (socialNetwork === 'facebook') {
      payload['idInstagram'] = idInstagram
    }
    return this.http.patch(`${this.url}/bind/${socialNetwork}`, payload, { headers }).catch((error: any) => {
      this.logService.error({ message: 'Error to renew token', error })
      throw error.response
    })
  }

  public async deleteSocialNetwork(socialNetwork: string, profileId: string): Promise<void> {
    let headers

    if (socialNetwork === 'auth0') {
      headers = await this.getHeaders(false, '', false)
    } else {
      headers = await this.getHeaders()
    }

    const URL = `${this.url}/bind/${socialNetwork}/${profileId}`
    return this.http
      .delete(URL, {
        headers,
      })
      .then((r: AxiosResponse) => r.data)
      .catch((error: any) => {
        this.logService.error({ message: 'Failed to delete social network', error })
        throw error
      })
  }

  public async getUserSocialNetworkProfiles(
    socialNetwork: 'instagram' | 'tiktok' | 'youtube' | 'twitter' | 'all' = 'all',
    page = 1,
    pageSize = 5
  ): Promise<SocialNetworkProfile[]> {
    const headers = await this.getHeaders()
    return this.http
      .get(`${this.url}/influencer/social-network-profiles?socialNetwork=${socialNetwork}&page=${page}&pageSize=${pageSize}`, { headers })
      .then((r: AxiosResponse) => r.data)
      .catch((error: any) => {
        this.logService.error({ message: 'Error to get user social network profiles', error })
        throw error
      })
  }

  async getRegisterCompleteness(squidId: string) {
    const profileData = await fetch(`${this.url}/creators-insights/completeness/${squidId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      cache: 'no-store',
    })
    return await profileData.json()
  }

  public async getProfiles(username: string): Promise<FacebookDiscoveryUser | undefined> {
    const headers = await this.getHeaders()
    return await this.http
      .get(`${this.url}/influencer/discovery/users/${username}`, { headers })
      .then((r: AxiosResponse) => r.data)
      .catch((error: any) => {
        this.logService.error({
          message: 'Failed to fetch profiles',
          error,
        })
        throw error
      })
  }
}
